import type React from "react";
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Typography} from "@mui/material";
import {PATH_SEP} from "~/constants";

type TaxonomyMoveCategoryDialogProps = {
    label: string;
    /** Every category below it, any depth: they move with it. */
    descendantCount: number;
    /** Names from L1 down to its current parent; empty when it is an L1. */
    fromPath: string[];
    /** Names from L1 down to the new parent; empty when it becomes an L1. */
    toPath: string[];
    /** Set when the moved branch reaches below the deepest level: the level numbers it adds. */
    newLevels?: {first: number, last: number};
    isSaving: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

const PathLine: React.FC<{caption: string, path: string[]}> = ({caption, path}) => (
    <Stack gap={0.5}>
        <Typography variant="caption" color="text.secondary">{caption}</Typography>
        <Typography variant="body2" fontWeight={path.length ? 600 : 400} color={path.length ? 'text.primary' : 'text.secondary'}>
            {path.length ? path.join(PATH_SEP) : 'L1'}
        </Typography>
    </Stack>
);

/** Confirms moving a category, with its whole branch, after it was dropped on another card. */
export const TaxonomyMoveCategoryDialog: React.FC<TaxonomyMoveCategoryDialogProps> = ({
                                                                                          label,
                                                                                          descendantCount,
                                                                                          fromPath,
                                                                                          toPath,
                                                                                          newLevels,
                                                                                          isSaving,
                                                                                          onCancel,
                                                                                          onConfirm,
                                                                                      }) => (
    <Dialog open onClose={isSaving ? undefined : onCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Move category</DialogTitle>
        <DialogContent>
            <Stack gap={2}>
                <DialogContentText>
                    Do you want to move <Box component="strong" sx={{color: 'text.primary'}}>{label}</Box>
                    {descendantCount > 0
                        ? <> and its {descendantCount} {descendantCount === 1 ? 'sub-category' : 'sub-categories'}?</>
                        : '?'}
                </DialogContentText>
                <PathLine caption="From" path={fromPath}/>
                <PathLine caption="To" path={toPath}/>
                {newLevels && (
                    // Same note as the Add dialog when it creates a level.
                    <Alert severity="info">
                        {newLevels.first === newLevels.last
                            ? <>This adds a new level to the taxonomy: L{newLevels.first}.</>
                            : <>This adds {newLevels.last - newLevels.first + 1} new levels to the taxonomy: L{newLevels.first}–L{newLevels.last}.</>}
                    </Alert>
                )}
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
            <Button variant="contained" onClick={onConfirm} loading={isSaving} autoFocus>
                Move
            </Button>
        </DialogActions>
    </Dialog>
);
